import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { CheckboxComponent } from '../../../@core/components/ui/checkbox/checkbox.component';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { SanitizeHtmlPipe } from '../../../@shared/pipes/sanitizeHtml.pipe';
import { SelectComponent, SelectOption, SelectConfig } from '../../../@core/components/ui/select';
import { MultiSelectComponent } from '../../../@core/components/ui/multi-select';
import { KnowledgeBaseService, IKnowledgeBaseStore } from '../../../contract-management/services/knowledgebase.service';
import { ProjectService } from '../../../contract-management/services/project.service';
import { KNOWLEDGE_BASE_SVG } from '../../../contract-management/components/settings/knowledgebase/knowledge-base-svg';
import { KNOWLEDGE_SCOPE } from '../../../contract-management/models/contract-management.model';
import { IProject } from '../../../@core/models/model';
import { ToastService } from '../../../@core/services/toast.service';

export type KnowledgeScope = 'workspace' | 'project';
export type UploadMode = 'withKB' | 'withoutKB';

export interface UploadEvent {
    knowledgeBaseIds?: string[];
    projectIds?: string[];
}

@Component({
    selector: 'app-knowledge-base-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SkeletonModule,
        TooltipModule,
        RadioButtonModule,
        ButtonComponent,
        CheckboxComponent,
        SearchBarComponent,
        SanitizeHtmlPipe,
        SelectComponent,
        MultiSelectComponent,
        DialogComponent
    ],
    templateUrl: './knowledge-base-dialog.component.html',
    styleUrl: './knowledge-base-dialog.component.scss'
})
export class KnowledgeBaseDialogComponent implements OnInit, OnChanges {
    private knowledgeBaseService = inject(KnowledgeBaseService);
    private projectService = inject(ProjectService);
    private toast = inject(ToastService);

    @Input() visible: boolean = false;
    @Input() scope: KnowledgeScope = 'workspace';
    @Input() workspace?: string | null;
    @Input() project?: string | null;
    @Input() documentCount: number = 0;
    @Input() uploading: boolean = false;

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() close = new EventEmitter<void>();
    @Output() upload = new EventEmitter<UploadEvent>();

    SVGS = KNOWLEDGE_BASE_SVG;

    // Upload mode: 'withKB' or 'withoutKB'
    uploadMode: UploadMode = 'withKB';

    // Internal state
    loading: boolean = false;
    searchValue: string = '';
    knowledgeBases: IKnowledgeBaseStore[] = [];
    filteredKnowledgeBases: IKnowledgeBaseStore[] = [];
    selectedKnowledgeBases: IKnowledgeBaseStore[] = [];
    // Track which project each KB belongs to (for grouping)
    kbProjectMap: Map<string, { projectId: string; projectName: string }> = new Map();

    // Project selection (for workspace documents in project knowledge)
    projects: IProject[] = [];
    projectOptions: SelectOption[] = [];
    selectedProjectId: string | null = null;
    selectedProjectIds: string[] = [];
    projectsLoading: boolean = false;
    projectSelectConfig: SelectConfig = {
        placeholder: 'Select project',
        searchable: true,
        clearable: false,
        style: { 'width': '100%' }
    };
    projectMultiSelectConfig: any = {
        placeholder: 'Select projects',
        searchable: true,
        clearable: false,
        style: { 'width': '100%' }
    };

    // Pagination
    skip: number = 0;
    limit: number = 20;
    total: number = 0;
    hasMore: boolean = true;
    isLoadingMore: boolean = false;

    // Create KB dialog
    createKbDialogVisible: boolean = false;
    createKbLoading: boolean = false;
    createKbForm: FormGroup = new FormGroup({
        name: new FormControl('', [Validators.required, Validators.maxLength(100)]),
        description: new FormControl('', [Validators.required, Validators.maxLength(500)])
    });
    get createKbControls() {
        return this.createKbForm.controls as {
            name: FormControl;
            description: FormControl;
        };
    }

    // Helper: Check if document has project
    get hasProject(): boolean {
        return !!this.project;
    }

    // Helper: Check if we need to show project dropdown
    get needsProjectSelection(): boolean {
        return this.scope === 'project' && !this.hasProject;
    }

    ngOnInit() {
        if (this.visible) {
            this.initializeDialog();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['visible'] && this.visible && !changes['visible'].previousValue) {
            // Dialog just opened
            this.reset();
            this.initializeDialog();
        }

        if (changes['scope'] || changes['workspace'] || changes['project']) {
            if (this.visible) {
                this.reset();
                this.initializeDialog();
            }
        }
    }

    async initializeDialog() {
        // Always fetch projects first to get project names for grouping
        await this.fetchProjects();

        if (this.needsProjectSelection) {
            // Project dropdown will be shown, KBs will be fetched after project selection
        } else {
            this.fetchKnowledgeBases(true);
        }
    }

    fetchProjects(): Promise<void> {
        return new Promise((resolve) => {
            if (this.projects.length > 0) {
                resolve();
                return;
            }
            this.projectsLoading = true;
            this.projectService.getProjects(0, 0, { active: true }).subscribe({
                next: (res) => {
                    if (res && res.success) {
                        this.projects = res.data.projects || [];
                        this.projectOptions = this.projects.map(project => ({
                            label: project.name,
                            value: project._id,
                            data: project
                        }));
                    }
                    this.projectsLoading = false;
                    resolve();
                },
                error: () => {
                    this.projectsLoading = false;
                    resolve();
                }
            });
        });
    }

    reset() {
        this.uploadMode = 'withKB';
        this.searchValue = '';
        this.knowledgeBases = [];
        this.filteredKnowledgeBases = [];
        this.selectedKnowledgeBases = [];
        this.selectedProjectId = null;
        this.selectedProjectIds = [];
        this.skip = 0;
        this.total = 0;
        this.hasMore = true;
        this.kbProjectMap.clear();
    }


    onProjectChange(projectId: string | null) {
        this.selectedProjectId = projectId;
        if (projectId && this.uploadMode === 'withKB') {
            // Only reset the available KBs list, keep selected KBs and their project mappings
            this.knowledgeBases = [];
            this.filteredKnowledgeBases = [];
            this.skip = 0;
            this.total = 0;
            this.hasMore = true;
            this.fetchKnowledgeBases(true);
        }
    }

    onProjectsChange(projectIds: string[]) {
        this.selectedProjectIds = projectIds;
        if (projectIds.length > 0 && this.uploadMode === 'withKB') {
            // Only reset the available KBs list, keep selected KBs and their project mappings
            this.knowledgeBases = [];
            this.filteredKnowledgeBases = [];
            this.skip = 0;
            this.total = 0;
            this.hasMore = true;
            this.fetchKnowledgeBases(true);
        }
    }

    resetKnowledgeBases() {
        this.knowledgeBases = [];
        this.filteredKnowledgeBases = [];
        this.selectedKnowledgeBases = [];
        this.skip = 0;
        this.total = 0;
        this.hasMore = true;
        this.kbProjectMap.clear();
    }

    fetchKnowledgeBases(reset: boolean = false) {
        // Don't fetch if project selection is required but not done
        if (this.needsProjectSelection) {
            if (this.uploadMode === 'withKB' && !this.selectedProjectId) {
                return;
            }
            if (this.uploadMode === 'withoutKB' && this.selectedProjectIds.length === 0) {
                return;
            }
        }

        if (reset) {
            this.skip = 0;
            this.knowledgeBases = [];
            this.hasMore = true;
        }

        if (!this.hasMore && !reset) {
            return;
        }

        if (reset) {
            this.loading = true;
        } else {
            this.isLoadingMore = true;
        }

        // Determine project ID(s) to use
        let projectId: string | null = null;
        if (this.scope === 'project') {
            if (this.hasProject) {
                projectId = this.project || null;
            } else if (this.uploadMode === 'withKB' && this.selectedProjectId) {
                projectId = this.selectedProjectId;
            }
        }

        const filters: any = {
            scope: this.scope === 'workspace' ? KNOWLEDGE_SCOPE.WORKSPACE : KNOWLEDGE_SCOPE.PROJECT,
            skip: this.skip,
            limit: this.limit
        };

        if (projectId) {
            filters.project = projectId;
        }

        if (this.searchValue.trim()) {
            filters.search = this.searchValue.trim();
        }

        this.knowledgeBaseService.getKnowledgeBaseStore(filters).subscribe({
            next: (res) => {
                if (res && res.success) {
                    const newBases = res.data?.stores || [];
                    this.total = res.data?.total || 0;

                    // Store project info for each KB
                    if (projectId) {
                        let projectName = 'Unknown Project';
                        const project = this.projects.find(p => p._id === projectId);
                        if (project) {
                            projectName = project.name;
                        } else if (this.hasProject && this.project === projectId) {
                            // If project not in list yet, we'll fetch it or use a placeholder
                            // For now, use placeholder - projects should be fetched before KBs
                        }
                        newBases.forEach(kb => {
                            if (!this.kbProjectMap.has(kb._id)) {
                                this.kbProjectMap.set(kb._id, { projectId, projectName });
                            }
                        });
                    }

                    if (reset) {
                        this.knowledgeBases = newBases;
                    } else {
                        this.knowledgeBases = [...this.knowledgeBases, ...newBases];
                    }

                    this.skip += newBases.length;
                    this.hasMore = this.knowledgeBases.length < this.total;
                    this.onSearch();
                } else {
                    this.hasMore = false;
                }
                this.loading = false;
                this.isLoadingMore = false;
            },
            error: () => {
                this.loading = false;
                this.isLoadingMore = false;
                this.hasMore = false;
            }
        });
    }

    onSearch() {
        const searchTerm = this.searchValue.toLowerCase().trim();
        if (!searchTerm) {
            this.filteredKnowledgeBases = this.knowledgeBases;
        } else {
            this.filteredKnowledgeBases = this.knowledgeBases.filter(kb =>
                kb.name?.toLowerCase().includes(searchTerm) ||
                kb.description?.toLowerCase().includes(searchTerm)
            );
        }
    }

    onSearchValueChange(value: string) {
        this.searchValue = value;
        // Debounce search - fetch from server
        this.resetKnowledgeBases();
        this.fetchKnowledgeBases(true);
    }

    onUploadModeChange(mode: UploadMode) {
        this.uploadMode = mode;
        if (mode === 'withKB') {
            if (this.needsProjectSelection && this.selectedProjectId) {
                this.resetKnowledgeBases();
                this.fetchKnowledgeBases(true);
            } else if (!this.needsProjectSelection) {
                this.resetKnowledgeBases();
                this.fetchKnowledgeBases(true);
            }
        } else {
            // Without KB mode - clear KB selections
            this.selectedKnowledgeBases = [];
        }
    }

    onScroll(event: Event) {
        if (this.uploadMode === 'withoutKB') return;

        const element = event.target as HTMLElement;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        // Check if near bottom (within 100px)
        const threshold = 100;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;

        if (isNearBottom && this.hasMore && !this.isLoadingMore && !this.loading) {
            this.fetchKnowledgeBases(false);
        }
    }

    onClose() {
        this.visibleChange.emit(false);
        this.close.emit();
    }

    ensureProjectInfoForKB(kb: IKnowledgeBaseStore) {
        // If project info is not already stored, add it
        if (!this.kbProjectMap.has(kb._id)) {
            let projectId: string | null = null;
            let projectName = 'Unknown Project';

            if (this.scope === 'project') {
                if (this.hasProject) {
                    projectId = this.project || null;
                    const project = this.projects.find(p => p._id === projectId);
                    projectName = project?.name || 'Unknown Project';
                } else if (this.selectedProjectId) {
                    projectId = this.selectedProjectId;
                    const project = this.projects.find(p => p._id === projectId);
                    projectName = project?.name || 'Unknown Project';
                }
            }

            if (projectId) {
                this.kbProjectMap.set(kb._id, { projectId, projectName });
            }
        }
    }

    toggleSelection(kb: IKnowledgeBaseStore, event?: Event) {
        if (event) {
            event.stopPropagation();
        }
        const index = this.selectedKnowledgeBases.findIndex(k => k._id === kb._id);
        if (index >= 0) {
            this.selectedKnowledgeBases.splice(index, 1);
        } else {
            this.selectedKnowledgeBases.push(kb);
            // Ensure project info is stored for this KB
            this.ensureProjectInfoForKB(kb);
        }
    }

    onCheckboxChange(kb: IKnowledgeBaseStore, event: { checked: boolean | any[] }) {
        const isChecked = typeof event.checked === 'boolean' ? event.checked : Boolean(event.checked);
        const index = this.selectedKnowledgeBases.findIndex(k => k._id === kb._id);

        if (isChecked && index === -1) {
            this.selectedKnowledgeBases.push(kb);
            // Ensure project info is stored for this KB
            this.ensureProjectInfoForKB(kb);
        } else if (!isChecked && index >= 0) {
            this.selectedKnowledgeBases.splice(index, 1);
        }
    }

    isSelected(kbId: string): boolean {
        return this.selectedKnowledgeBases.some(kb => kb._id === kbId);
    }

    isAllSelected(): boolean {
        return this.filteredKnowledgeBases.length > 0 &&
            this.filteredKnowledgeBases.every(kb => this.isSelected(kb._id));
    }

    onToggleSelectAll(event: { checked: boolean | any[] }): void {
        const isChecked = typeof event.checked === 'boolean' ? event.checked : Boolean(event.checked);

        if (isChecked) {
            this.filteredKnowledgeBases.forEach(kb => {
                if (!this.isSelected(kb._id)) {
                    this.selectedKnowledgeBases.push(kb);
                    // Ensure project info is stored for this KB
                    this.ensureProjectInfoForKB(kb);
                }
            });
        } else {
            this.filteredKnowledgeBases.forEach(kb => {
                const index = this.selectedKnowledgeBases.findIndex(k => k._id === kb._id);
                if (index >= 0) {
                    this.selectedKnowledgeBases.splice(index, 1);
                }
            });
        }
    }

    removeSelection(kb: IKnowledgeBaseStore) {
        const index = this.selectedKnowledgeBases.findIndex(k => k._id === kb._id);
        if (index >= 0) {
            this.selectedKnowledgeBases.splice(index, 1);
        }
    }

    // Group selected KBs by project
    getGroupedKnowledgeBases(): { projectId: string; projectName: string; kbs: IKnowledgeBaseStore[] }[] {
        if (this.scope === 'workspace') {
            return [];
        }

        const grouped: { [key: string]: { projectId: string; projectName: string; kbs: IKnowledgeBaseStore[] } } = {};

        this.selectedKnowledgeBases.forEach(kb => {
            // Get project info from map
            const projectInfo = this.kbProjectMap.get(kb._id);

            let projectId = '';
            let projectName = '';

            if (this.hasProject) {
                projectId = this.project || '';
                const project = this.projects.find(p => p._id === projectId);
                projectName = project?.name || 'Unknown Project';
            } else if (projectInfo) {
                projectId = projectInfo.projectId;
                projectName = projectInfo.projectName;
            }

            if (projectId) {
                if (!grouped[projectId]) {
                    grouped[projectId] = { projectId, projectName, kbs: [] };
                }
                grouped[projectId].kbs.push(kb);
            }
        });

        return Object.values(grouped);
    }

    // Get KBs for single project (no grouping needed)
    getSingleProjectKBs(): IKnowledgeBaseStore[] {
        return this.selectedKnowledgeBases;
    }

    // Check if we should show project grouping
    shouldShowProjectGrouping(): boolean {
        if (this.scope !== 'project' || this.selectedKnowledgeBases.length === 0) {
            return false;
        }

        // If document has project, all KBs belong to same project - no grouping needed
        if (this.hasProject) {
            return false;
        }

        // Check if KBs belong to multiple projects
        const projectIds = new Set<string>();
        this.selectedKnowledgeBases.forEach(kb => {
            const projectInfo = this.kbProjectMap.get(kb._id);
            if (projectInfo) {
                projectIds.add(projectInfo.projectId);
            }
        });

        return projectIds.size > 1;
    }

    // Check if upload button should be enabled
    isUploadEnabled(): boolean {
        if (this.uploadMode === 'withKB') {
            // For "With KB", need KBs selected
            // If project selection is needed, also need project selected
            if (this.needsProjectSelection && !this.selectedProjectId) {
                return false;
            }
            return this.selectedKnowledgeBases.length > 0;
        } else {
            // Without KB
            if (this.scope === 'workspace') {
                return true; // Always enabled for workspace
            } else {
                // Project scope
                if (this.hasProject) {
                    return true; // Document already has project
                } else {
                    return this.selectedProjectIds.length > 0; // Need project selection
                }
            }
        }
    }

    onUpload() {
        if (!this.isUploadEnabled()) {
            return;
        }

        if (this.uploadMode === 'withKB') {
            const knowledgeBaseIds = this.selectedKnowledgeBases.map(kb => kb._id);
            this.upload.emit({ knowledgeBaseIds });
        } else {
            // Without KB
            if (this.scope === 'workspace') {
                this.upload.emit({ knowledgeBaseIds: [] });
            } else {
                // Project scope
                if (this.hasProject && this.project) {
                    // Document already has project - send single project as array
                    this.upload.emit({ knowledgeBaseIds: [], projectIds: [this.project] });
                } else {
                    // Workspace document -> need to emit selected projectIds
                    this.upload.emit({ knowledgeBaseIds: [], projectIds: this.selectedProjectIds });
                }
            }
        }
    }

    getTitle(): string {
        const scopeText = this.scope === 'workspace' ? 'workspace knowledge' : 'project knowledge';
        return `Add to ${scopeText} (${this.documentCount} files)`;
    }

    getScopeLabel(): string {
        return this.scope === 'workspace' ? 'Workspace knowledge' : 'Project knowledge';
    }

    getIcon(): string {
        return this.scope === 'workspace'
            ? this.SVGS.KNOWLEDGE_BASE_ICON
            : this.SVGS.OPEN_BOOK_CHECK;
    }

    getProjectSelectConfig(): SelectConfig {
        return {
            ...this.projectSelectConfig,
            disabled: this.projectsLoading
        };
    }

    getProjectMultiSelectConfig(): any {
        return {
            ...this.projectMultiSelectConfig,
            disabled: this.projectsLoading
        };
    }

    onCreateKnowledgeBase() {
        this.createKbForm.reset();
        this.createKbDialogVisible = true;
    }

    closeCreateKbDialog() {
        this.createKbDialogVisible = false;
        this.createKbForm.reset();
    }

    createKnowledgeBase() {
        if (this.createKbForm.invalid) {
            this.createKbForm.markAllAsTouched();
            return;
        }

        this.createKbLoading = true;
        const formData = new FormData();
        formData.append('name', this.createKbForm.value.name.trim());
        formData.append('description', this.createKbForm.value.description.trim());
        formData.append('scope', this.scope === 'workspace' ? KNOWLEDGE_SCOPE.WORKSPACE : KNOWLEDGE_SCOPE.PROJECT);

        const projectId = this.project || this.selectedProjectId;
        if (projectId) {
            formData.append('project', projectId);
        }

        this.knowledgeBaseService.createKnowledgeBaseStore(formData).subscribe((res) => {
            if (res.success && res.data) {
                this.toast.success('Knowledge base created successfully');
                this.closeCreateKbDialog();
                this.resetKnowledgeBases();
                this.fetchKnowledgeBases(true);
            } else {
                this.toast.error('Failed to create knowledge base');
            }
            this.createKbLoading = false;
        });
    }
}